import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Loader from "../Loader/Loader";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import { CURRENCY_CODE } from "../../utils/constant";

const getCurrencyLabel = (currency) => {
  if (!currency) return "";
  const symbol = CURRENCY_CODE[currency];
  return symbol ? `${symbol} ${currency}` : currency;
};

const GiftCardOrderEditModal = ({ show, handleClose, order }) => {
  const [formData, setFormData] = useState({ amount: "", email: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { AsyncPatchAPICall } = useApi();

  useEffect(() => {
    if (show && order) {
      setFormData({
        amount: order.amount != null ? String(order.amount) : "",
        email: order.email || "",
      });
      setErrors({});
    }
  }, [show, order]);

  const validate = () => {
    const newErrors = {};
    const amount = Number(formData.amount);
    if (!formData.amount || Number.isNaN(amount)) {
      newErrors.amount = "Amount is required";
    } else if (amount < 10) {
      newErrors.amount = "Minimum amount is 10";
    } else if (amount > 5000) {
      newErrors.amount = "Maximum amount is 5000";
    }
    if (!formData.email?.trim()) {
      newErrors.email = "Recipient email is required to resend";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    if (!validate() || !order?._id) return;
    setLoading(true);

    const result = await AsyncPatchAPICall(
      `/giftcard/order/${order._id}/update-resend`,
      {
        amount: Number(formData.amount),
        email: formData.email.trim().toLowerCase(),
      }
    );

    if (result?.errors) {
      toast.error(result.errors[0]);
    } else if (result?.success) {
      toast.success(result.message || "Gift card updated and resent.");
      handleClose(true);
    }

    setLoading(false);
  };

  const disabled = order?.isRedeemed;

  return (
    <>
      {loading && <Loader />}
      <Modal show={show} onHide={() => handleClose(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update amount &amp; resend</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {order?.giftCardUniqueCode && (
            <p className="text-sm text-secondary-light mb-16">
              Code: <strong>{order.giftCardUniqueCode}</strong>
            </p>
          )}
          {disabled && (
            <p className="text-danger-600 text-sm mb-16">
              This gift card has been redeemed and cannot be updated.
            </p>
          )}
          <Form>
            <Form.Group className="mb-16">
              <Form.Label>Amount</Form.Label>
              <div className="input-group">
                <span className="input-group-text bg-base text-secondary-light">
                  {getCurrencyLabel(order?.currency) || "—"}
                </span>
                <Form.Control
                  type="number"
                  name="amount"
                  min={10}
                  max={5000}
                  value={formData.amount}
                  onChange={handleChange}
                  disabled={disabled}
                  isInvalid={!!errors.amount}
                />
              </div>
              {errors.amount && (
                <div className="invalid-feedback d-block">{errors.amount}</div>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>Recipient email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={disabled}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={disabled || loading}
          >
            Update &amp; resend
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GiftCardOrderEditModal;
